import { api } from "@/shared/api/api";
import type { TApiOpportunityPayload } from "@/shared/api/types/clientPortal";

const opportunitiesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getOpportunities: builder.query<TApiOpportunityPayload, string>({
      query: (eid) => `/lease/opportunities/${eid}`,
      providesTags: (_result, _e, eid) => [{ type: "Opportunity", id: eid }],
    }),
    updateIgnoredLeases: builder.mutation<
      void,
      {
        cognitoId: string;
        eid: string;
        ignoredOpportunities: Array<string>;
      }
    >({
      query: ({ cognitoId, eid, ignoredOpportunities }) => ({
        url: `/user/settings/${cognitoId}/ignoredOpportunities`,
        method: "PUT",
        body: {
          eid,
          ignoredOpportunities,
        },
      }),
      async onQueryStarted({ cognitoId, eid, ignoredOpportunities }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (api.util.updateQueryData as any)("getPolicyForCognitoUser", cognitoId, (draft: any) => {
            Object.assign(draft, {
              settings: {
                ...draft.settings,
                ignoredOpportunities: {
                  ...draft.settings.ignoredOpportunities,
                  [eid]: ignoredOpportunities,
                },
              },
            });
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    optIntoLease: builder.mutation<
      void,
      {
        eid: string;
        leaseSeries: string;
        leaseName: string;
        leaseNumber: string;
      }
    >({
      query: ({ eid, leaseSeries, leaseName, leaseNumber }) => ({
        url: `/lease/optout/${eid}/${leaseSeries}/${leaseName}/${leaseNumber}`,
        method: "POST",
      }),
      async onQueryStarted({ eid, leaseSeries, leaseName, leaseNumber }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          opportunitiesApi.util.updateQueryData("getOpportunities", eid, (draft) => {
            const leaseInstance = `${leaseSeries}/${leaseName}#${leaseNumber}`;

            Object.assign(draft, {
              [leaseInstance]: {
                ...draft[leaseInstance],
                optout: "in",
              },
            });
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _e, { eid }) => [{ type: "Opportunity", id: eid }],
    }),
    optOutOfLease: builder.mutation<
      void,
      {
        eid: string;
        leaseSeries: string;
        leaseName: string;
        leaseNumber: string;
        leaseFeedback: string;
      }
    >({
      query: ({ eid, leaseSeries, leaseName, leaseNumber, leaseFeedback = "" }) => ({
        url: `/lease/optout/${eid}/${leaseSeries}/${leaseName}/${leaseNumber}`,
        method: "DELETE",
        body: {
          leaseFeedback,
        },
      }),
      async onQueryStarted({ eid, leaseSeries, leaseName, leaseNumber }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          opportunitiesApi.util.updateQueryData("getOpportunities", eid, (draft) => {
            const leaseInstance = `${leaseSeries}/${leaseName}#${leaseNumber}`;

            Object.assign(draft, {
              [leaseInstance]: {
                ...draft[leaseInstance],
                optout: "out",
              },
            });
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _e, { eid }) => [{ type: "Opportunity", id: eid }],
    }),
    endReviewPeriod: builder.mutation<void, string>({
      query: (eid) => ({
        url: `/lease/review_period/${eid}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _e, eid) => [
        { type: "Opportunity", id: eid },
        { type: "AccountInfo", id: eid },
      ],
    }),
    submitDesiredAllocation: builder.mutation<
      { result: string; desiredAllocation: number; leaseInstance: string },
      { leaseInstance: string; desiredAllocation: number; accountId: string; leaseName: string }
    >({
      query: ({ accountId, ...body }) => ({
        url: `/lease/desired-allocation/${accountId}`,
        method: "POST",
        body,
      }),
    }),
    createUnsecuredLeaseAgreement: builder.mutation<
      { token: string; documentId: string },
      {
        eid: string;
        leaseInstance: string;
        agreementTemplateId: string;
        signerEmail: string;
        leaseName: string;
        accountName: string;
      }
    >({
      query: ({ eid, ...body }) => ({
        url: `/lease/unsecured-agreement/${eid}`,
        method: "POST",
        body,
      }),
    }),
    submitUnsecuredLeaseAgreement: builder.mutation<
      { result: string },
      { eid: string; leaseInstance: string; documentId: string; signerEmail: string }
    >({
      query: ({ eid, ...body }) => ({
        url: `/lease/unsecured-agreement/${eid}/submit`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _e, { eid }) => [{ type: "Opportunity", id: eid }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetOpportunitiesQuery,
  useUpdateIgnoredLeasesMutation,
  useOptIntoLeaseMutation,
  useOptOutOfLeaseMutation,
  useEndReviewPeriodMutation,
  useSubmitDesiredAllocationMutation,
  useCreateUnsecuredLeaseAgreementMutation,
  useSubmitUnsecuredLeaseAgreementMutation,
  endpoints,
} = opportunitiesApi;
